import { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate, useNavigationType, type NavigateOptions, type To } from 'react-router-dom'

type Direction = 'forward' | 'back' | 'none'

const pathStack: string[] = []
let isBackNavigation = false

export function popPreviousPath(): string | null {
  isBackNavigation = true
  return pathStack.pop() ?? null
}

// Leaving a mode (festival mode) for `destination`: the mode's own routes and any
// entry for the destination itself come off the stack, so back from there returns
// to wherever the user was before entering the mode. Call just before navigating.
export function unwindPathStack(isModePath: (path: string) => boolean, destination: string) {
  isBackNavigation = true
  const isDestination = (path: string) =>
    path.startsWith(destination) && /^([/?#]|$)/.test(path.slice(destination.length))
  while (pathStack.length > 0) {
    const top = pathStack[pathStack.length - 1]
    if (!isModePath(top) && !isDestination(top)) break
    pathStack.pop()
  }
}

// A REPLACE navigation (a page's tab synced into ?tab=, a redirect) swaps the
// current entry rather than adding one, so it pushes nothing — otherwise back
// from a page would step through its own tab switches.
export function useTrackPreviousPath() {
  const { pathname, search, hash } = useLocation()
  const navigationType = useNavigationType()
  const prev = useRef(pathname + search + hash)
  useEffect(() => {
    const current = pathname + search + hash
    if (current !== prev.current) {
      if (isBackNavigation) {
        isBackNavigation = false
      } else if (navigationType !== 'REPLACE') {
        pathStack.push(prev.current)
      }
      prev.current = current
    }
  }, [pathname, search, hash, navigationType])
}

export function setTransitionDirection(dir: Direction | 'pop') {
  document.documentElement.dataset.navDirection = dir
}

function supportsViewTransitions(): boolean {
  return (
    'startViewTransition' in document &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

let startViewTransition: Document['startViewTransition'] | undefined
let inFlight: ViewTransition | null = null

// The router starts the transitions but never handles `ready`, which rejects when a
// transition is skipped (a newer navigation, the app backgrounding). The navigation
// itself still lands, so the rejection isn't an error worth reporting.
if (typeof document !== 'undefined' && 'startViewTransition' in document) {
  const start = document.startViewTransition.bind(document)
  startViewTransition = ((...args: Parameters<typeof start>) => {
    const transition = start(...args)
    transition.ready.catch(() => {})
    inFlight = transition
    transition.finished.finally(() => {
      if (inFlight === transition) inFlight = null
    })
    return transition
  }) as typeof start
  document.startViewTransition = startViewTransition
}

let expectingPop = false

// React Router replays a view transition when history pops back to a route it
// animated into, and defers that render through several effects before the
// transition even starts — after an iOS swipe, which already animated to the
// previous page, the outgoing page painted again for a few frames (#639). So a
// browser back hides startViewTransition from the router until it has handled
// the pop (its no-loader navigation settles in microtasks) and renders the page
// directly; only our own navigate(-n) keeps its animation. The 'pop' direction
// keeps any transition that still slips through instant.
// A back that lands while the forward transition is still running keeps it
// visible: rendered directly, the pop is a low-priority update that the pending
// forward render (flushSync) overtakes, leaving the URL back but the page forward.
// The router's own interruption handling gets that case right.
// Capture phase so this runs before the router's own popstate listener.
if (typeof window !== 'undefined') {
  window.addEventListener(
    'popstate',
    () => {
      if (expectingPop) {
        expectingPop = false
        return
      }
      setTransitionDirection('pop')
      if (!startViewTransition || inFlight) return
      const restore = startViewTransition
      ;(document as { startViewTransition?: unknown }).startViewTransition = undefined
      setTimeout(() => {
        document.startViewTransition = restore
      })
    },
    { capture: true },
  )
}

export function useTransitionNavigate() {
  const navigate = useNavigate()

  const transitionNavigate = useCallback(
    (to: To | number, options?: NavigateOptions & { direction?: Direction }) => {
      const dir = options?.direction ?? (typeof to === 'number' && to < 0 ? 'back' : 'forward')
      // Tab switches skip the crossfade: mid-transition the page is a snapshot, so the
      // floating nav's backdrop blur has nothing to sample and flashes clear.
      const animate = dir !== 'none' && supportsViewTransitions() && document.visibilityState !== 'hidden'
      if (animate) setTransitionDirection(dir)
      if (typeof to === 'number') {
        expectingPop = animate
        navigate(to)
        return
      }
      // The router pushes the entry synchronously and wraps only the render in the
      // view transition. Pushing from inside a startViewTransition callback runs
      // outside the tap's user gesture, and WebKit's back gesture skips such
      // entries — swiping back from an artist jumped past the event (#559).
      navigate(to, { ...options, flushSync: true, viewTransition: animate })
    },
    [navigate],
  )

  return transitionNavigate
}
